import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuth } from "../../contexts/AuthContext";
import { authService } from "../../services";
import { ScrollNavbar } from "../../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faUser,
  faEnvelope,
  faPhone,
  faLock,
} from "@fortawesome/free-solid-svg-icons";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{10}$/;

const Profile = () => {
  const { user, updateUser } = useAuth();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (!user) {
      history.push("/login");
      return;
    }

    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const response = await authService.getProfile();
        const profile = response.data || user;
        setName(profile.name || "");
        setEmail(profile.email || "");
        setPhone(profile.phone || "");
      } catch (err: any) {
        // Fall back to the already-cached account info from login/OTP.
        setName(user.name || "");
        setEmail(user.email || "");
        setPhone(user.phone || "");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancelEdit = () => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setPhone(user.phone || "");
    }
    setIsEditing(false);
  };

  const handleSaveProfile = async () => {
    if (!name.trim() || !email.trim() || !phone.trim()) {
      Swal.fire("Missing Details", "Name, email, and phone are all required.", "warning");
      return;
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      Swal.fire("Invalid Email", "Please enter a valid email address.", "warning");
      return;
    }
    if (!PHONE_REGEX.test(phone.trim())) {
      Swal.fire("Invalid Phone Number", "Please enter a valid 10-digit phone number.", "warning");
      return;
    }

    setIsSavingProfile(true);
    try {
      const response = await authService.updateProfile({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });

      if (response.success) {
        if (user) {
          updateUser({ ...user, name: name.trim(), email: email.trim(), phone: phone.trim() });
        }
        setIsEditing(false);
        Swal.fire({
          icon: "success",
          title: "Profile Updated",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire("Error", response.message || "Failed to update profile.", "error");
      }
    } catch (err: any) {
      Swal.fire("Error", err.message || "Failed to update profile.", "error");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      Swal.fire("Missing Details", "Please fill in all password fields.", "warning");
      return;
    }
    if (newPassword.length < 6) {
      Swal.fire("Weak Password", "New password must be at least 6 characters.", "warning");
      return;
    }
    if (newPassword !== confirmPassword) {
      Swal.fire("Passwords Don't Match", "New password and confirmation must match.", "warning");
      return;
    }
    if (!user) return;

    setIsSavingPassword(true);
    try {
      const response = await authService.changePassword(user.id, {
        oldPassword,
        newPassword,
      });

      if (response.success) {
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        Swal.fire({
          icon: "success",
          title: "Password Changed",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire("Error", response.message || "Failed to change password.", "error");
      }
    } catch (err: any) {
      Swal.fire("Error", err.message || "Failed to change password.", "error");
    } finally {
      setIsSavingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon icon={faSpinner} className="text-5xl text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ScrollNavbar />
      <div className="max-w-3xl mx-auto py-12 px-4 sm:px-6 lg:px-8 space-y-8">
        <h1 className="text-3xl font-mazzardbold text-gray-800">My Profile</h1>

        {/* Basic Details */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Basic Details</h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                Edit
              </button>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="profile_name" className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-400" />
                Name
              </label>
              <input
                type="text"
                id="profile_name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!isEditing}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label htmlFor="profile_email" className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-gray-400" />
                Email
              </label>
              <input
                type="email"
                id="profile_email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!isEditing}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label htmlFor="profile_phone" className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon icon={faPhone} className="mr-2 text-gray-400" />
                Phone Number
              </label>
              <input
                type="tel"
                id="profile_phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={!isEditing}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
              {isEditing && (
                <p className="mt-1 text-xs text-gray-500">
                  Changing your phone number will require re-verification on your next OTP login.
                </p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleCancelEdit}
                disabled={isSavingProfile}
                className="rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={isSavingProfile}
                className="rounded-md bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSavingProfile ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )}
        </div>

        {/* Password Reset */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            <FontAwesomeIcon icon={faLock} className="mr-2 text-gray-400" />
            Change Password
          </h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="old_password" className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                id="old_password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                id="new_password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirm_password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Signed up or logged in via OTP and never set a password? Use "Forgot Password" on the
            login page instead.
          </p>
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleChangePassword}
              disabled={isSavingPassword}
              className="rounded-md bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isSavingPassword ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
